<i18n src="./resources/locales.json"></i18n>

<template>
  <EcsSideBarWrapper
    ref="helpSideBar"
    v-intersect="(entries, observer) => onIntersect(entries, observer)"
    class="help-side-bar"
    :search-placeholder="$t('searchPlaceholder')"
    @search-input-changed="handleSearch"
  >
    <v-row no-gutters>
      <v-col>
        <EcsSideBarTileContent
          v-for="(topicContent, topic) in filteredHelpTopics"
          :key="topic"
          ref="group"
          :header="$t(`${topic}.company`)"
        >
          <v-list class="help-topic-content" two-line>
            <v-list-item
              v-for="helpItem in topicContent"
              :key="helpItem.id"
              ref="subgroup"
              dense
              @click="helpItem.action ? helpItem.action() : ''"
            >
              <v-list-item-content :dark="$vuetify.theme.dark">
                <v-list-item-title class="sidebar-title">
                  {{ helpItem.titleI18nKey ? $t(`${topic}.${helpItem.titleI18nKey}`) : helpItem.title }}
                </v-list-item-title>
                <v-list-item-subtitle class="sub-title">
                  {{ helpItem.subTitleI18nKey ? $t(`${topic}.${helpItem.subTitleI18nKey}`) : helpItem.subTitle }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </EcsSideBarTileContent>
      </v-col>
    </v-row>
  </EcsSideBarWrapper>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { getPlatform, getBrowser } from '@/components/ecs-app-side-bar/ecs-help-side-bar/agentInfoUtils';
import { openExternalLinks } from '@/utils';

export default {
  name: 'EcsHelpSideBar',

  data() {
    return {
      helpTopics: () => ({}),
      searchString: '',
      platform: {},
      browser: {},
      helpDocumentationLoaded: false,
      helpDocumentationSorting: [
        'overview',
        'agents',
        'contacts',
        'crm',
        'chats',
        'telephony',
        'callbacks',
        'notifications',
        'settings'
      ]
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),
    ...mapGetters('SYSTEM', ['productVersion']),

    filteredHelpTopics() {
      let result = {};

      if (this.searchString) {
        Object.entries(this.helpTopics).forEach(([key, value]) => {
          if (key === 'applicationInformation') {
            result[key] = value;
          } else {
            result[key] = this.filteredResult(value, key);
          }
        });
      } else {
        result = this.helpTopics;
      }

      return result;
    }
  },

  mounted() {
    this.helpTopics = {
      helpDocumentation: [],
      tour: [
        {
          id: 'welcomeTour',
          titleI18nKey: 'welcomeTour',
          subTitleI18nKey: 'welcomeTourSubtitle',
          action: this.startTour
        }
      ],
      applicationInformation: [
        {
          id: 'ecs-web-client-version',
          title: null,
          subTitle: process.env.APPLICATION_VERSION.replace(/\.0$/, '').replace(/\+.*$/, ''),
          action: this.openReleaseNotes
        },
        {
          id: 'ecs-platform-version',
          titleI18nKey: 'ecsPlatformVersion',
          subTitle: null
        },
        {
          id: 'browser',
          titleI18nKey: 'browser',
          subTitle: `${this.browser.name} ${this.browser.version}`
        },
        {
          id: 'operatingSystem',
          titleI18nKey: 'operatingSystem',
          subTitle: `${this.platform.name}`
        }
      ]
    };
  },

  created() {
    this.platform = getPlatform();
    this.browser = getBrowser();
  },

  methods: {
    ...mapActions('SETTINGS', ['updateShowApplicationTour']),

    onIntersect(entries) {
      if (entries[0] && entries[0].isIntersecting) {
        // Replace with a real tour when we have it
        this.removeWallboardTour();
        !this.helpDocumentationLoaded && this.getHelpDocumentation();
        this.getClientData();
        this.getConfigurationData();
        this.getAgentInformation();
      }
    },

    handleSearch(searchString) {
      this.searchString = searchString;
    },

    removeWallboardTour() {
      if (this.loggedInAgent.isWallboard) {
        delete this.helpTopics.tour;
      }
    },

    getConfigurationData() {
      const platformVersionItem = this.helpTopics.applicationInformation.find(
        item => item.id === 'ecs-platform-version'
      );
      platformVersionItem.subTitle = this.productVersion;
    },

    getClientData() {
      const agent = this.loggedInAgent.isWallboard ? 'eWallboard' : 'eAgent';
      const clientVersionItem = this.helpTopics.applicationInformation.find(
        item => item.id === 'ecs-web-client-version'
      );
      clientVersionItem.title = this.$t('applicationInformation.ecsWebClientVersion', { agent });
    },

    getHelpDocumentation() {
      const helpDocumentUrl = this.loggedInAgent.isWallboard
        ? 'https://support.evolveip.net/display/EIQ/Evolve+Contact+Suite+-+eWallboard'
        : 'https://support.evolveip.net/display/EIQ/Evolve+Contact+Suite+-+eAgent';

      const agent = this.loggedInAgent.isWallboard ? 'eWallboard' : 'eAgent';

      this.helpTopics.helpDocumentation = [
        {
          index: 0,
          title: this.$t('helpDocumentation.evolveIPKnowledgeBase'),
          subTitle: this.$t('helpDocumentation.evolveContactSuiteWebAgent', { agent }),
          action: () => {
            openExternalLinks([helpDocumentUrl]);
          }
        }
      ];
    },

    getAgentInformation() {
      if (this.loggedInAgent.isWallboard) {
        this.helpTopics.agentInformation = [
          {
            id: 'ecs-agent-information',
            titleI18nKey: 'ecsAgentInformation',
            subTitle: this.loggedInAgent.username
          }
        ];
      }
    },

    startTour() {
      this.$root.$emit('start-tour');
    },

    openReleaseNotes() {
      this.$root.$emit('open-release-notes');
    },

    filteredResult(value, key) {
      return value.filter(document => {
        return (
          new RegExp(this.searchString, 'i').test(
            document.title ? document.title : this.$t(`${key}.${document.titleI18nKey}`)
          ) ||
          new RegExp(this.searchString, 'i').test(
            document.subTitle ? document.subTitle : this.$t(`${key}.${document.subTitleI18nKey}`)
          )
        );
      });
    }
  }
};
</script>

<style scoped>
.v-list-item {
  transition: all 0.2s ease;
}

.v-list-item:hover {
  background-color: var(--v-hover-background);
}

.side-bar-item:not(:last-child) {
  border-bottom: 1px solid #0003;
}

.sidebar-title {
  font-size: 12px !important;
  text-transform: uppercase;
  font-weight: bold;
  opacity: 0.5;
}

[dark] .sub-title {
  color: #fffe !important;
}

.sub-title {
  font-size: 16px;
  line-height: 20px;
}

.help-topic-content {
  margin-top: -16px;
}

.empty-content {
  border-radius: 8px !important;
  border: 2px dashed;
  text-align: center;
  opacity: 0.5;
  width: calc(100% - 32px);
}
</style>
